import { useState, useEffect, useCallback, useMemo } from "preact/hooks";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, LinearProgress, Alert, Stack
} from "@mui/material";
import PaymentIcon from "@mui/icons-material/Payment";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { fetchToServer } from "@shared/components";
import { BillingFields } from "./BillingFields";
import { SpeiInfo } from "./SpeiInfo";
import { usePaymentsStore } from "./store";

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PK);

function fmtMoney(amount, currency = "MXN") {
  const cur = String(currency || "MXN").toUpperCase();
  return new Intl.NumberFormat("es-MX", { style: "currency", currency: cur }).format(Number(amount || 0));
}

function CheckoutForm({ onPaid, payLabel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setError(null);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setError(submitError.message);
      setIsProcessing(false);
      return;
    }

    const { error: confirmError } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: `${window.location.origin}/payment-success` },
      redirect: "if_required",
    });

    if (confirmError) {
      setError(confirmError.message);
      setIsProcessing(false);
      return;
    }

    onPaid?.();
    setIsProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      <DialogActions sx={{ pt: 2 }}>
        <Button
          type="submit"
          disabled={!stripe || isProcessing}
          variant="contained"
          startIcon={<PaymentIcon />}
          aria-label={payLabel ? `Pagar ${payLabel}` : "Pagar"}
        >
          {isProcessing ? "Procesando..." : (payLabel ? `Pagar ${payLabel}` : "Pagar")}
        </Button>
      </DialogActions>
    </form>
  );
}

export function PayDialog({ open, onClose, payment, onPaid }) {
  const { role, projectId, defaultMethod, setDefaultMethod, spei, setSpei } = usePaymentsStore();

  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState("AUTO"); // AUTO → usa default; CARD → Stripe

  const [wantsInvoice, setWantsInvoice] = useState(true);
  const [fiscal, setFiscal] = useState({
    rfc: "",
    razon: "",
    cp: "",
    uso: "G03",
    regimen: "612",
  });

  const payLabel = payment ? fmtMoney(payment.amountTotal, payment.currency) : null;

  useEffect(() => {
    if (!open) {
      // Reset state on close
      setTimeout(() => {
        setClientSecret(null);
        setError(null);
        setLoading(false);
        setMode("AUTO");
      }, 300);
    }
  }, [open]);

  const handleGoToPay = useCallback(async () => {
    if (!open || !payment?._id) return;

    if (wantsInvoice) {
      if (!/^[A-Z&Ñ]{3,4}\d{6}[A-Z0-9]{3}$/.test(String(fiscal.rfc || "").toUpperCase())) {
        setError("RFC inválido");
        return;
      }
      if (!fiscal.razon?.trim()) { setError("La razón social es requerida"); return; }
      if (!/^\d{5}$/.test(String(fiscal.cp || ""))) { setError("El código postal debe ser de 5 dígitos"); return; }
      if (!fiscal.uso?.trim()) { setError("El uso CFDI es requerido"); return; }
    }

    setLoading(true);
    setError(null);
    setClientSecret(null);

    const metadata = wantsInvoice ? {
      invoice_requested: "si",
      fiscal_rfc: String(fiscal.rfc || "").toUpperCase(),
      fiscal_razon: fiscal.razon || "",
      fiscal_cp: String(fiscal.cp || ""),
      fiscal_uso: fiscal.uso || "G03",
      fiscal_regimen: fiscal.regimen || "612",
    } : { invoice_requested: "no" };

    try {
      const useCard = mode === "CARD" || defaultMethod === "CARD";
      if (useCard) {
        const url = payment.paymentNumber === 1
          ? `/${role}/api/projects/${projectId}/payments/init-intent`
          : `/${role}/api/projects/${projectId}/payments/${payment._id}/next-intent`;

        const { clientSecret: cs } = await fetchToServer({ url, method: "POST", data: { metadata } });
        setClientSecret(cs);
        setMode("CARD");
      } else {
        const res = await fetchToServer({
          url: `/${role}/api/payments/spei-attempt`,
          method: "POST",
          data: { projectId, paymentId: payment._id },
        });
        setSpei({
          reference: res.reference,
          eligibility: res.eligibility,
          status: res.status,
          paymentId: res.paymentId,
          paymentNo: res.paymentNo,
          hostedInstructionsUrl: res.hostedInstructionsUrl,
          paymentIntentId: res.paymentIntentId,
        });
        setDefaultMethod("BANK_TRANSFER");
        setMode("SPEI");
      }
    } catch (err) {
      setError(err.message || "No fue posible iniciar el pago");
    } finally {
      setLoading(false);
    }
  }, [open, payment, wantsInvoice, fiscal, role, projectId]);

  const handlePaid = useCallback(() => {
    // Delegar al padre para que maneje la lógica post-pago
    onPaid?.();
  }, [onPaid]);

  const options = useMemo(() => ({ clientSecret, appearance: { theme: "stripe" } }), [clientSecret]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {clientSecret ? "Realizar pago" : "Datos de facturación"}
      </DialogTitle>

      <DialogContent>
        {loading && <LinearProgress sx={{ mb: 1 }} />}
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

        {!clientSecret && mode !== "SPEI" ? (
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <BillingFields
              fiscal={fiscal}
              setFiscal={setFiscal}
              wantsInvoice={wantsInvoice}
              setWantsInvoice={setWantsInvoice}
            />
            <DialogActions sx={{ px: 0, gap: 1 }}>
              <Button onClick={onClose}>Cancelar</Button>
              <Button variant="outlined" onClick={() => setMode("CARD")}>Tarjeta</Button>
              <Button variant="contained" startIcon={<PaymentIcon />} onClick={handleGoToPay} disabled={loading}>
                Continuar ({defaultMethod === "BANK_TRANSFER" ? "SPEI" : "Tarjeta"}){payLabel ? ` • ${payLabel}` : ""}
              </Button>
            </DialogActions>
          </Stack>
        ) : null}

        {mode === "SPEI" && spei?.reference && (
          <SpeiInfo
            reference={spei.reference}
            amountLabel={payLabel}
            eligibility={spei.eligibility}
            onUseCard={() => {
              setMode("CARD");
              setClientSecret(null);
              setError(null);
            }}
            hostedInstructionsUrl={spei.hostedInstructionsUrl}
          />
        )}

        {clientSecret && mode === "CARD" && (
          <Elements stripe={stripePromise} options={options}>
            <CheckoutForm onPaid={handlePaid} payLabel={payLabel} />
          </Elements>
        )}
      </DialogContent>
    </Dialog>
  );
}
